import React from "react";
import { useTranslation } from "react-i18next";
import { getUserId, getUserName, getUserAvatar } from "../../utils/chatHelpers";
import { color } from "../../styles/theme";

/**
 * Horizontal avatar strip with an online/offline status dot, fed by the
 * SignalR `GetChatRoomUsersWithStatus` response + live `UserOnline`/
 * `UserOffline` events (see ChatContext). Ported from mobile's
 * `participants/index.js` + `user-cell`.
 */
const ParticipantsStrip = ({ participants, presenceMap }) => {
  const { t } = useTranslation("chat");
  if (!participants?.length) return null;

  return (
    <div style={s.strip}>
      {participants.map((p, i) => {
        const userId = getUserId(p);
        const name = getUserName(p) || "?";
        const avatar = getUserAvatar(p);
        const online = presenceMap?.[userId] ?? p.isOnline ?? p.IsOnline ?? false;

        return (
          <div key={userId ?? i} style={s.item} title={`${name} · ${online ? t("participants.online") : t("participants.offline")}`}>
            <div style={s.avatarWrap}>
              {avatar ? (
                <img src={avatar} alt="" style={s.avatar} onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div style={s.avatarFallback}>{name.charAt(0).toUpperCase()}</div>
              )}
              <span style={{ ...s.dot, background: online ? color.success500 : color.gray400 }} />
            </div>
            <span style={s.name}>{name}</span>
          </div>
        );
      })}
    </div>
  );
};

const s = {
  strip: { display: "flex", gap: 14, padding: "10px 16px", overflowX: "auto", borderBottom: `1px solid ${color.border}`, flexShrink: 0 },
  item: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 52, flexShrink: 0 },
  avatarWrap: { position: "relative" },
  avatar: { width: 36, height: 36, borderRadius: "50%", objectFit: "cover", display: "block" },
  avatarFallback: {
    width: 36, height: 36, borderRadius: "50%", background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13,
  },
  dot: { position: "absolute", bottom: 0, right: 0, width: 9, height: 9, borderRadius: "50%", border: `2px solid ${color.surface}` },
  name: { fontSize: 10, color: color.textMuted, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 52 },
};

export default ParticipantsStrip;
